import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsImage } from "react-icons/bs";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import { Skeleton } from "../../../Components/Needed";
import { useTrackedForm, useUnsaved } from "../unsaved";

const SiteDetailsTab = () => {
    const { getSiteDetails } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { form, setForm, loadForm, dirty, discard } = useTrackedForm({ siteName: "", siteTitle: "" });
    const [logoPreview, setLogoPreview] = useState("");
    const [favIconPreview, setFavIconPreview] = useState("");
    const logoFileRef = useRef(null);
    const favIconFileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/admin/business/view/site-details" });
        if (response?.success) {
            const data = response?.data?.data || {};
            loadForm({ siteName: data.siteName || "", siteTitle: data.siteTitle || "" });
            setLogoPreview(data.logo || "");
            setFavIconPreview(data.favIcon || "");
        }
        setLoading(false);
    }, [loadForm]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleFile = (e, previewSetter) => {
        const file = e.target.files?.[0];
        if (!file) return;
        previewSetter(URL.createObjectURL(file));
    };

    const onSave = async () => {
        setSaving(true);
        const fd = new FormData();
        if (form.siteName?.trim()) fd.append("siteName", form.siteName.trim());
        if (form.siteTitle?.trim()) fd.append("siteTitle", form.siteTitle.trim());
        if (logoFileRef.current?.files?.[0]) fd.append("logo", logoFileRef.current.files[0]);
        if (favIconFileRef.current?.files?.[0]) fd.append("favIcon", favIconFileRef.current.files[0]);

        const response = await Handler({ method: "patch", url: "/admin/business/site-details", data: fd, formdata: true });
        if (response?.success) {
            toast.success(response?.data?.message || "Site details updated");
            await Promise.all([load(), getSiteDetails?.()]);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update site details"));
        }
        setSaving(false);
    };

    useUnsaved("site", dirty, onSave, discard);

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <div className="settings-card">
            <h5 className="fw-semibold mb-3">Site Details</h5>

            <div className="row g-4">
                <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="settings-site-name">Site name</label>
                    <input
                        id="settings-site-name"
                        type="text"
                        name="siteName"
                        className="form-control"
                        placeholder="e.g. GetMyDrive"
                        value={form.siteName}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="settings-site-title">Browser tab title</label>
                    <input
                        id="settings-site-title"
                        type="text"
                        name="siteTitle"
                        className="form-control"
                        placeholder="e.g. GetMyDrive Admin"
                        value={form.siteTitle}
                        onChange={handleChange}
                    />
                </div>

                <div className="col-12 col-md-6">
                    <label className="form-label d-block">Logo</label>
                    <div className="settings-image-uploader">
                        <div className="settings-image-preview">
                            {logoPreview ? <img src={logoPreview} alt="Logo preview" /> : <BsImage />}
                        </div>
                        <input
                            ref={logoFileRef}
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) => handleFile(e, setLogoPreview)}
                        />
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <label className="form-label d-block">Favicon</label>
                    <div className="settings-image-uploader">
                        <div className="settings-image-preview settings-image-preview-sm">
                            {favIconPreview ? <img src={favIconPreview} alt="Favicon preview" /> : <BsImage />}
                        </div>
                        <input
                            ref={favIconFileRef}
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) => handleFile(e, setFavIconPreview)}
                        />
                    </div>
                </div>
            </div>

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
    );
};

export default SiteDetailsTab;
